import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Spinner from "@components/ui/spinner";
import {
  MediaLibCategoryEnum,
  MediaLibPresetEnum,
  MediaLibWebsiteCopyrightDialog,
  useMediaLibImportImages,
  useMediaLibUploadFiles,
} from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppSelector } from "@store";
import { isDialogOpen } from "@store/dialogs/selectors";
import { useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import type { IMediaFile } from "@/shared/types";
import {
  useMediaLibUploadingStore,
  useMediaLibWebsiteStoreShallow,
} from "../../stores";
import { useMediaLibImportStore } from "../../stores/media-lib-import.store";
import { MediaLibUploadWebsite } from "./upload-website";

interface IProps {
  onUploadStart?: () => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSelectedFolderId: (value: string | null) => void;
  onAddFiles: (value: IMediaFile[]) => void;
  onCleanSearch?: () => void;
  selectedFolderId?: string | null;
  moveToCategory?: MediaLibCategoryEnum;
  controller: AbortController;
  preset?: MediaLibPresetEnum;
}

interface IOpt {
  key: "files" | "website" | "presentation";
  label: string;
  icon: string;
  selectedFolderId?: string | null;
}

const OPTIONS: IOpt[] = [
  {
    key: "files",
    label: "Upload from my computer",
    icon: "ri-upload-cloud-2-line",
  },
  {
    key: "website",
    label: "Import from website",
    icon: "ri-global-line",
  },
  {
    key: "presentation",
    label: "Import from PPT/PDF",
    icon: "ri-file-download-line",
  },
];

export const UploadBtnContainer = ({
  onUploadStart,
  setCategory,
  setSelectedFolderId,
  onAddFiles,
  controller,
  selectedFolderId,
  moveToCategory = MediaLibCategoryEnum.AllFiles,
  onCleanSearch,
  preset = MediaLibPresetEnum.All,
}: IProps) => {
  const [isPicking, setIsPicking] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    isImportingWebsite,
    setIsImportingWebsite,
    websiteUrl,
    setWebsiteUrl,
  } = useMediaLibWebsiteStoreShallow((s) => ({
    isImportingWebsite: s.isImporting,
    setIsImportingWebsite: s.setIsImporting,
    websiteUrl: s.websiteUrl,
    setWebsiteUrl: s.setWebsiteUrl,
  }));
  const isUploadingFiles = useMediaLibUploadingStore(
    (s) => s.uploadingFiles.length > 0,
  );
  const isImportingPdf = useMediaLibImportStore((s) => s.isImporting);
  const isWebsiteDialogOpen = useAppSelector(
    isDialogOpen(MediaLibWebsiteCopyrightDialog.dialogId),
  );

  const { openFilePicker } = useMediaLibUploadFiles({
    accept:
      preset === MediaLibPresetEnum.OnlyImages
        ? "image/*"
        : preset === MediaLibPresetEnum.OnlyVideos
          ? "video/mp4"
          : "image/*,video/mp4",
    multiple: true,
    moveToCategory,
    onUploadStart,
    setCategory,
    setSelectedFolderId,
    onAddFiles,
    selectedFolderId,
  });

  const { openImportFilePicker } = useMediaLibImportImages({
    onUploadStart,
    setCategory,
    setSelectedFolderId,
    onAddFiles,
  });

  useOnClickOutside(containerRef, () => {
    if (isWebsiteDialogOpen) return;
    setIsPicking(false);
    if (!isImportingWebsite) {
      setWebsiteUrl(null);
    }
  });

  const handleAction = (action: IOpt["key"]) => {
    switch (action) {
      case "files":
        openFilePicker();
        setIsPicking(false);
        onCleanSearch?.();
        break;
      case "presentation":
        openImportFilePicker();
        setIsPicking(false);
        onCleanSearch?.();
        break;
      case "website":
        setIsPicking(false);
        setWebsiteUrl("");
        onCleanSearch?.();
        break;
    }
  };

  const showWebsiteUrl = websiteUrl != null || isImportingWebsite;
  const isLoader = isUploadingFiles || isImportingPdf;
  return (
    <div className="tw-relative" ref={containerRef}>
      <Button
        id="media-lib-upload-btn"
        variant="default"
        size="sm"
        className={cn("tw-w-full tw-text-[13px]", isLoader && "tw-cursor-wait")}
        onClick={isLoader ? undefined : () => setIsPicking((p) => !p)}
      >
        {isLoader ? (
          <>
            <Spinner className="tw-mr-2 tw-size-[14px] tw-border-white" />
            Uploading...
          </>
        ) : (
          <>
            <Icon
              icon="ri-add-line"
              className="tw-mr-1.5 tw-text-[16px]/none tw-text-white"
            />
            Add media
          </>
        )}
      </Button>

      {(isPicking || showWebsiteUrl) && (
        <div className="tw-absolute tw-right-0 tw-top-full tw-z-50 tw-mt-2 tw-w-auto tw-min-w-max tw-rounded-lg tw-border tw-border-blue-100 tw-bg-white tw-p-2 tw-shadow-popover">
          <ol className="tw-m-0 tw-grid tw-w-full tw-list-none tw-content-start tw-gap-1">
            {!showWebsiteUrl ? (
              OPTIONS.map((opt) => (
                <li
                  key={opt.key}
                  onClick={() => handleAction(opt.key)}
                  className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-3 tw-py-2 hover:tw-bg-blue-50"
                >
                  <Icon
                    icon={opt.icon}
                    className="tw-mr-2 tw-text-[16px]/none tw-text-blue-500"
                  />
                  <span className="tw-text-[11px]">{opt.label}</span>
                </li>
              ))
            ) : (
              <MediaLibUploadWebsite
                onUploadStart={onUploadStart}
                setCategory={setCategory}
                setSelectedFolderId={setSelectedFolderId}
                isImportingWebsite={isImportingWebsite}
                setIsImportingWebsite={setIsImportingWebsite}
                websiteUrl={websiteUrl}
                setWebsiteUrl={setWebsiteUrl}
                controller={controller}
              />
            )}
          </ol>
        </div>
      )}
    </div>
  );
};
